import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'react-hot-toast';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import {
  FiUsers,
  FiDollarSign,
  FiCreditCard,
  FiMessageSquare,
  FiCamera,
  FiTrendingUp,
  FiTrendingDown,
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiEye,
  FiDownload,
  FiRefreshCw
} from 'react-icons/fi';

// Import shared admin components
import {
  AdminContainer,
  AdminHeader,
  HeaderContent,
  HeaderText,
  HeaderActions,
  ActionButton,
  MainContent,
  StatsGrid,
  StatCard,
  StatHeader,
  StatIcon,
  TrendIndicator,
  StatValue,
  StatLabel,
  ChartsGrid,
  ChartCard,
  ChartHeader,
  ChartFilters,
  FilterButton,
  TwoColumnGrid,
  ActivityFeed,
  ActivityList,
  ActivityItem,
  ActivityIcon,
  ActivityContent,
  AlertsPanel,
  AlertItem,
  AlertIcon,
  AlertText,
  QuickActions,
  QuickAction,
  LoadingSpinner,
  ErrorMessage,
  CHART_COLORS
} from '../../styles/AdminComponents';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revenueData, setRevenueData] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [timeFilter, setTimeFilter] = useState('7d');
  const adminRank = localStorage.getItem('adminRank');

  useEffect(() => {
    fetchDashboardData();
  }, [timeFilter]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Only call the endpoints that exist
      const statsRes = await axios.get('/api/admin/dashboard-stats', { headers });

      if (statsRes.data.success) {
        setStats(statsRes.data.stats);
      }

      // Try to get revenue data, but don't fail if endpoint doesn't exist
      try {
        const revenueRes = await axios.get(`/api/admin/revenue-data?period=${timeFilter}`, { headers });
        if (revenueRes.data.success) {
          setRevenueData(revenueRes.data.data || []);
        }
      } catch (revenueError) {
        console.log('Revenue data endpoint not available yet');
        // Generate sample data for demo purposes
        const sampleData = Array.from({ length: 7 }, (_, i) => ({
          date: new Date(Date.now() - (6 - i) * 24 * 60 * 60 * 1000).toLocaleDateString(),
          revenue: Math.floor(Math.random() * 1000) + 500
        }));
        setRevenueData(sampleData);
      }

      // Try to get activity data, but don't fail if endpoint doesn't exist
      try {
        const activityRes = await axios.get('/api/admin/recent-activity', { headers });
        if (activityRes.data.success) {
          setRecentActivity(activityRes.data.activities || []);
        }
      } catch (activityError) {
        console.log('Recent activity endpoint not available yet');
        // Generate sample activity for demo purposes
        setRecentActivity([
          {
            action: 'user_created',
            description: 'New user registered',
            timestamp: new Date(Date.now() - 1000 * 60 * 30)
          },
          {
            action: 'payment_confirmed',
            description: 'Payment confirmed for user ABC123',
            timestamp: new Date(Date.now() - 1000 * 60 * 60)
          },
          {
            action: 'admin_login',
            description: 'Admin logged in',
            timestamp: new Date(Date.now() - 1000 * 60 * 90)
          }
        ]);
      }

      // Generate sample alerts (you can replace this with real data)
      setAlerts([
        {
          id: 1,
          type: 'warning',
          message: '5 payments pending confirmation',
          timestamp: new Date()
        },
        {
          id: 2,
          type: 'success',
          message: '12 new users registered today',
          timestamp: new Date()
        },
        {
          id: 3,
          type: 'error',
          message: '2 failed payment webhooks need attention',
          timestamp: new Date()
        }
      ]);

      setLoading(false);
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  const refreshData = () => {
    fetchDashboardData();
    toast.success('Dashboard refreshed');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const getActivityIcon = (action) => {
    switch (action) {
      case 'user_created': return { icon: FiUsers, color: '#10b981', bg: '#dcfce7' };
      case 'payment_confirmed': return { icon: FiCheckCircle, color: '#10b981', bg: '#dcfce7' };
      case 'payment_failed': return { icon: FiXCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'admin_login': return { icon: FiActivity, color: '#3b82f6', bg: '#dbeafe' };
      default: return { icon: FiActivity, color: '#6b7280', bg: '#f1f5f9' };
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner>
          <FiRefreshCw className="spinner" />
          Loading dashboard...
        </LoadingSpinner>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <ErrorMessage>{error}</ErrorMessage>
      </AdminLayout>
    );
  }

  if (!stats) return null;

  return (
    <AdminLayout>
      <AdminContainer>
        <AdminHeader>
          <HeaderContent>
            <HeaderText>
              <h1>Welcome back, {adminRank}!</h1>
              <p>Here's what's happening with your platform today.</p>
            </HeaderText>
            <HeaderActions>
              <ActionButton onClick={refreshData}>
                <FiRefreshCw />
                Refresh
              </ActionButton>
              <ActionButton>
                <FiDownload />
                Export Report
              </ActionButton>
            </HeaderActions>
          </HeaderContent>
        </AdminHeader>

        <MainContent>
          <StatsGrid>
            <StatCard accentColor={CHART_COLORS.primary}>
              <StatHeader>
                <StatIcon bgColor="#dbeafe" iconColor={CHART_COLORS.primary}>
                  <FiUsers />
                </StatIcon>
                <TrendIndicator positive>
                  <FiTrendingUp />
                  +12%
                </TrendIndicator>
              </StatHeader>
              <StatValue>{formatNumber(stats.users)}</StatValue>
              <StatLabel>Total Users</StatLabel>
            </StatCard>

            <StatCard accentColor={CHART_COLORS.success}>
              <StatHeader>
                <StatIcon bgColor="#dcfce7" iconColor={CHART_COLORS.success}>
                  <FiDollarSign />
                </StatIcon>
                <TrendIndicator positive>
                  <FiTrendingUp />
                  +8.2%
                </TrendIndicator>
              </StatHeader>
              <StatValue>{formatCurrency(stats.totalRevenue)}</StatValue>
              <StatLabel>Total Revenue</StatLabel>
            </StatCard>

            <StatCard accentColor={CHART_COLORS.warning}>
              <StatHeader>
                <StatIcon bgColor="#fef3c7" iconColor={CHART_COLORS.warning}>
                  <FiCreditCard />
                </StatIcon>
                <TrendIndicator positive={stats.payments > 100}>
                  <FiTrendingUp />
                  +3.1%
                </TrendIndicator>
              </StatHeader>
              <StatValue>{formatNumber(stats.payments)}</StatValue>
              <StatLabel>Total Payments</StatLabel>
            </StatCard>

            <StatCard accentColor={CHART_COLORS.purple}>
              <StatHeader>
                <StatIcon bgColor="#ede9fe" iconColor={CHART_COLORS.purple}>
                  <FiMessageSquare />
                </StatIcon>
                <TrendIndicator positive={stats.feedback < 50}>
                  <FiTrendingDown />
                  -2.4%
                </TrendIndicator>
              </StatHeader>
              <StatValue>{formatNumber(stats.feedback)}</StatValue>
              <StatLabel>Feedback Items</StatLabel>
            </StatCard>

            <StatCard accentColor={CHART_COLORS.pink}>
              <StatHeader>
                <StatIcon bgColor="#fce7f3" iconColor={CHART_COLORS.pink}>
                  <FiCamera />
                </StatIcon>
                <TrendIndicator positive>
                  <FiTrendingUp />
                  +5.7%
                </TrendIndicator>
              </StatHeader>
              <StatValue>{formatNumber(stats.cameraUsers)}</StatValue>
              <StatLabel>Camera Users</StatLabel>
            </StatCard>

            <StatCard accentColor={CHART_COLORS.teal}>
              <StatHeader>
                <StatIcon bgColor="#cffafe" iconColor={CHART_COLORS.teal}>
                  <FiActivity />
                </StatIcon>
                <TrendIndicator positive>
                  <FiTrendingUp />
                  +15.3%
                </TrendIndicator>
              </StatHeader>
              <StatValue>{stats.mostPopularPlan || 'Monthly'}</StatValue>
              <StatLabel>Popular Plan</StatLabel>
            </StatCard>
          </StatsGrid>

          <ChartsGrid>
            <ChartCard>
              <ChartHeader>
                <h3>Revenue Overview</h3>
                <ChartFilters>
                  <FilterButton 
                    active={timeFilter === '7d'} 
                    onClick={() => setTimeFilter('7d')}
                  >
                    7 Days
                  </FilterButton>
                  <FilterButton 
                    active={timeFilter === '30d'} 
                    onClick={() => setTimeFilter('30d')}
                  >
                    30 Days
                  </FilterButton>
                  <FilterButton 
                    active={timeFilter === '90d'} 
                    onClick={() => setTimeFilter('90d')}
                  >
                    90 Days
                  </FilterButton>
                </ChartFilters>
              </ChartHeader>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                    labelStyle={{ color: '#374151' }}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke={CHART_COLORS.primary}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorRevenue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard>
              <ChartHeader>
                <h3>Payment Status</h3>
              </ChartHeader>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Confirmed', value: stats.paymentBreakdown?.confirmed || 0, color: CHART_COLORS.success },
                      { name: 'Pending', value: stats.paymentBreakdown?.pending || 0, color: CHART_COLORS.warning },
                      { name: 'Expired', value: stats.paymentBreakdown?.expired || 0, color: CHART_COLORS.error }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {[
                      { name: 'Confirmed', value: stats.paymentBreakdown?.confirmed || 0, color: CHART_COLORS.success },
                      { name: 'Pending', value: stats.paymentBreakdown?.pending || 0, color: CHART_COLORS.warning },
                      { name: 'Expired', value: stats.paymentBreakdown?.expired || 0, color: CHART_COLORS.error }
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [formatNumber(value), 'Payments']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsGrid>

          <TwoColumnGrid>
            <ActivityFeed>
              <h3>Recent Activity</h3>
              <ActivityList>
                {recentActivity.length > 0 ? recentActivity.map((activity, index) => {
                  const iconConfig = getActivityIcon(activity.action);
                  const IconComponent = iconConfig.icon;
                  
                  return (
                    <ActivityItem key={index}>
                      <ActivityIcon bgColor={iconConfig.bg} iconColor={iconConfig.color}>
                        <IconComponent />
                      </ActivityIcon>
                      <ActivityContent>
                        <div className="activity-text">{activity.description}</div>
                        <div className="activity-time">
                          {new Date(activity.timestamp).toLocaleString()}
                        </div>
                      </ActivityContent>
                    </ActivityItem>
                  );
                }) : (
                  <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
                    No recent activity
                  </div>
                )}
              </ActivityList>
            </ActivityFeed>

            <AlertsPanel>
              <h3>System Alerts</h3>
              {alerts.map(alert => (
                <AlertItem key={alert.id} type={alert.type}>
                  <AlertIcon type={alert.type}>
                    {alert.type === 'error' && <FiXCircle />}
                    {alert.type === 'warning' && <FiAlertTriangle />}
                    {alert.type === 'success' && <FiCheckCircle />}
                  </AlertIcon>
                  <AlertText>{alert.message}</AlertText>
                </AlertItem>
              ))}
            </AlertsPanel>
          </TwoColumnGrid>

          <QuickActions>
            <QuickAction to="/admin/users">
              <FiUsers className="icon" />
              <span className="text">Manage Users</span>
            </QuickAction>
            {adminRank === 'owner' && (
              <>
                <QuickAction to="/admin/payments">
                  <FiCreditCard className="icon" />
                  <span className="text">View Payments</span>
                </QuickAction>
                <QuickAction to="/admin/camera">
                  <FiCamera className="icon" />
                  <span className="text">Camera Users</span>
                </QuickAction>
                <QuickAction to="/admin/hwid">
                  <FiEye className="icon" />
                  <span className="text">HWID System</span>
                </QuickAction>
              </>
            )}
            <QuickAction to="/admin/feedback">
              <FiMessageSquare className="icon" />
              <span className="text">View Feedback</span>
            </QuickAction>
          </QuickActions>
        </MainContent>
      </AdminContainer>
    </AdminLayout>
  );
};

export default DashboardPage; 